/**
 * What: View-model types for the Shopify Tests index (`/tests`).
 * Transport / data-flow: Plain data derived from saved builder drafts, setup history, run status, and result
 *   readiness. Every field is a real fact from one of those sources; nothing here is a placeholder or an estimate.
 */
import type { StudySetupStatusTone } from '../study-setup-lifecycle'

/** Which stored object a row came from. Rows are never merged across sources. */
export type TestsIndexSource = 'study'

/** The one supported operation a row offers. `read-only` carries the merchant-facing reason there is none. */
export type TestsIndexAction =
  | { kind: 'open-study'; studyId: string; status: string }
  | { kind: 'read-only'; reason: string }

/** Completed/planned survey responses aggregated across the selected arms, shown only when valid. */
export interface TestsIndexResponseProgress {
  completedResponses: number
  plannedResponses: number
  /** Whole-number percentage, rounded down so an unfinished run never reads 100%. */
  percent: number
}

/** Server-confirmed milestone of an active run (`latestRun.stage`, #3983). Never derived on the client. */
export type TestsIndexStage = 'waiting' | 'preparing_shoppers' | 'asking' | 'scoring' | 'writing_report'

/** Milestone order shown on a running card. */
export const TESTS_INDEX_STAGES: readonly TestsIndexStage[] = [
  'waiting',
  'preparing_shoppers',
  'asking',
  'scoring',
  'writing_report',
]

/** A packet whose latest status refresh failed. `kind` follows the structured activity failure. */
export interface TestsIndexStatusIssue {
  studyId: string
  kind: 'http' | 'network' | 'invalid'
  status?: number
  code?: string
}

export interface TestsIndexRecord {
  /** Source-qualified and stable: `${source}:${id}`. */
  key: string
  source: TestsIndexSource
  title: string
  /** Product the test is about, when it differs from the title. */
  productTitle: string | null
  /** Treatment kind label, only when the saved setup supplies it. */
  kindLabel: string | null
  imageUrl: string | null
  imageAlt: string
  statusLabel: string
  statusTone: StudySetupStatusTone
  /** A dated phrase such as "Saved Oct 7, 2:05 pm", or null when the source has no usable date. */
  timeLabel: string | null
  /** Plain explanation of the row's state when it has no further action (failed, cancelled, preparing, ...). */
  detail: string | null
  /** Packet and run identity, present only when the source supplies them. */
  runId: string | null
  /** Valid aggregate response progress; null means unknown (never zero). */
  progress: TestsIndexResponseProgress | null
  /** Server-confirmed milestone for an active run; null means unknown (no checklist). */
  stage: TestsIndexStage | null
  statusIssue: TestsIndexStatusIssue | null
  action: TestsIndexAction
}

export interface TestsIndexRecords {
  /** Saved drafts and setups that have no queued, running, or ready run. */
  saved: TestsIndexRecord[]
  /** Setups whose latest run is queued or running. */
  active: TestsIndexRecord[]
  /** Setups whose latest run is report-ready: one latest run per packet. */
  results: TestsIndexRecord[]
}
